import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useOrderLogsQuery, type OrderLogEntry } from '../hooks/useOrderLogsQuery';

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return iso || '-';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ACTOR_FALLBACK: Record<string, string> = { CUSTOMER: '고객', SYSTEM: 'System', ADMIN: '관리자', LEGACY: '고도몰' };

/** 처리자 — 관리자 ID·고객 아이디·System, 아래 줄에 IP(고도몰처럼). */
const ActorCell = ({ entry }: { entry: OrderLogEntry }) => (
  <div className="flex flex-col leading-tight">
    <span>{entry.actorName ?? ACTOR_FALLBACK[entry.actorType] ?? entry.actorType}</span>
    {entry.actorIp ? <span className="text-[11px] text-[color:var(--content-muted)]">{entry.actorIp}</span> : null}
  </div>
);

/**
 * QA-552 [주문 로그] — 고도몰 "주문 로그 보기"와 같은 표(일자·처리자·상품명·변경상태·변경 사유), 최근 것이 위.
 * 연 뒤에만 불러온다. BE 반영 전이면 안내만 보인다.
 */
export const OrderLogDialog = ({ orderId, open, onOpenChange }: { orderId: string; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const query = useOrderLogsQuery(orderId, open);
  const rows = [...(query.data ?? [])].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="order-log-dialog">
        <DialogHeader>
          <DialogTitle>주문 로그</DialogTitle>
          <DialogDescription>최초 주문부터 상태 변경·취소·환불까지 이 주문에 일어난 변경과 처리자입니다.</DialogDescription>
        </DialogHeader>

        {query.isPending ? (
          <Skeleton className="h-[160px] w-full" />
        ) : query.isError ? (
          <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
            {friendlyMessage(query.error, '주문 로그를 불러오지 못했습니다.')}
          </p>
        ) : query.data === null ? (
          <p className="text-sm text-[color:var(--content-muted)]" data-testid="order-log-not-ready">
            주문 로그는 서버 반영 후 볼 수 있습니다.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-[color:var(--content-muted)]" data-testid="order-log-empty">
            남은 주문 로그가 없습니다.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table data-testid="order-log-table">
              <TableHeader style={{ background: 'var(--surface-1)' }}>
                <TableRow>
                  <TableHead scope="col" className="w-[130px]">일자</TableHead>
                  <TableHead scope="col" className="w-[140px]">처리자</TableHead>
                  <TableHead scope="col">상품명</TableHead>
                  <TableHead scope="col" className="w-[180px]">변경상태</TableHead>
                  <TableHead scope="col" className="w-[200px]">변경 사유</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.occurredAt}-${i}`} data-testid="order-log-row">
                    <TableCell className="whitespace-nowrap tabular-nums text-[12px]">{formatDateTime(r.occurredAt)}</TableCell>
                    <TableCell className="text-[12px]">
                      <ActorCell entry={r} />
                    </TableCell>
                    <TableCell className="text-[12px]">{r.productName ?? '-'}</TableCell>
                    <TableCell className="text-[12px]">
                      {r.beforeLabel ? `${r.beforeLabel} > ` : '> '}
                      {r.afterLabel ?? '-'}
                    </TableCell>
                    <TableCell className="text-[12px]">{r.reason ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
